import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  Typography,
  Divider,
} from '@mui/material';
import { Locale } from '../../../services/workout.service';
import { MetActivityOptions, MetCompendiumType } from '../../../services/met-activity.service';

interface MetActivityDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  initialData?: any;
  options: MetActivityOptions;
  isLoading: boolean;
  title: string;
  submitLabel: string;
}

const LOCALES: Locale[] = ['uk', 'en', 'ru', 'cs', 'es', 'pl', 'be'];

export const MetActivityDialog: React.FC<MetActivityDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  options,
  isLoading,
  title,
  submitLabel,
}) => {
  const [code, setCode] = useState('');
  const [compendiumType, setCompendiumType] = useState<MetCompendiumType>('adult');
  const [categoryCode, setCategoryCode] = useState<number | ''>('');
  const [categoryName, setCategoryName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [nameI18n, setNameI18n] = useState<Partial<Record<Locale, string>>>({});
  const [metValue, setMetValue] = useState<number | ''>('');
  const [youthAgeGroup, setYouthAgeGroup] = useState<number | ''>('');

  useEffect(() => {
    if (open) {
      if (initialData) {
        setCode(initialData.code || '');
        setCompendiumType(initialData.compendiumType || 'adult');
        setCategoryCode(initialData.categoryCode ?? '');
        setCategoryName(initialData.categoryName || '');
        setNameEn(initialData.nameEn || '');
        const i18n: Partial<Record<Locale, string>> = {};
        LOCALES.forEach(loc => { i18n[loc] = initialData.nameI18n?.[loc] || ''; });
        setNameI18n(i18n);
        setMetValue(initialData.metValue ?? '');
        setYouthAgeGroup(initialData.youthAgeGroup ?? '');
      } else {
        setCode('');
        setCompendiumType('adult');
        setCategoryCode('');
        setCategoryName('');
        setNameEn('');
        const i18n: Partial<Record<Locale, string>> = {};
        LOCALES.forEach(loc => { i18n[loc] = ''; });
        setNameI18n(i18n);
        setMetValue('');
        setYouthAgeGroup('');
      }
    }
  }, [open, initialData]);

  const handleSubmit = async () => {
    const cleanedNameI18n = Object.fromEntries(
      Object.entries(nameI18n).filter(([, v]) => v && (v as string).trim() !== '')
    ) as { [key in Locale]?: string };

    await onSubmit({
      code: code.trim(),
      compendiumType,
      categoryCode: categoryCode === '' ? undefined : Number(categoryCode),
      categoryName: categoryName.trim(),
      nameEn: nameEn.trim(),
      nameI18n: Object.keys(cleanedNameI18n).length > 0 ? cleanedNameI18n : undefined,
      metValue: metValue === '' ? undefined : Number(metValue),
      youthAgeGroup: compendiumType === 'youth' && youthAgeGroup !== '' ? Number(youthAgeGroup) : undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={3}>
            {/* Left Column: English name + Translations */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Назва</Typography>
              <TextField
                margin="dense"
                label="Назва (English, оригінал)"
                type="text"
                fullWidth
                variant="outlined"
                value={nameEn}
                onChange={e => setNameEn(e.target.value)}
                required
                size="small"
              />

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Переклади</Typography>
              {LOCALES.map(lang => (
                <TextField
                  key={lang}
                  margin="dense"
                  label={`Назва (${lang.toUpperCase()})`}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={nameI18n[lang] || ''}
                  onChange={e => setNameI18n({ ...nameI18n, [lang]: e.target.value })}
                  size="small"
                />
              ))}
            </Grid>

            {/* Right Column: Properties */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Властивості</Typography>

              <TextField
                margin="dense"
                label="Код"
                type="text"
                fullWidth
                variant="outlined"
                value={code}
                onChange={e => setCode(e.target.value)}
                required
                inputProps={{ maxLength: 6 }}
                helperText="До 6 символів"
                size="small"
              />

              <FormControl fullWidth margin="dense" size="small">
                <InputLabel>Компендіум</InputLabel>
                <Select
                  value={compendiumType}
                  label="Компендіум"
                  onChange={e => {
                    setCompendiumType(e.target.value as MetCompendiumType);
                    if (e.target.value !== 'youth') setYouthAgeGroup('');
                  }}
                >
                  {options.compendiumTypes.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <TextField
                    margin="dense"
                    label="Код категорії"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={categoryCode}
                    onChange={e => setCategoryCode(e.target.value === '' ? '' : Number(e.target.value))}
                    required
                    inputProps={{ min: 1, max: 99 }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    margin="dense"
                    label="Назва категорії"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={categoryName}
                    onChange={e => setCategoryName(e.target.value)}
                    required
                    inputProps={{ maxLength: 100 }}
                    size="small"
                  />
                </Grid>
              </Grid>

              <TextField
                margin="dense"
                label="MET значення"
                type="number"
                fullWidth
                variant="outlined"
                value={metValue}
                onChange={e => setMetValue(e.target.value === '' ? '' : Number(e.target.value))}
                required
                inputProps={{ min: 0.1, step: 0.1 }}
                helperText="Мін. 0.1, до 2 знаків після коми"
                size="small"
              />

              {compendiumType === 'youth' && (
                <FormControl fullWidth margin="dense" size="small" required>
                  <InputLabel>Вікова група</InputLabel>
                  <Select
                    value={youthAgeGroup}
                    label="Вікова група"
                    onChange={e => setYouthAgeGroup(e.target.value as number | '')}
                  >
                    {options.youthAgeGroups.map(g => (
                      <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button onClick={handleSubmit} disabled={isLoading} variant="contained">
          {isLoading ? <CircularProgress size={24} /> : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
